import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  confirmVendorInvoice,
  deleteVendorInvoice,
  fetchVendorInvoice,
  fetchVendorList,
  resubmitVendorInvoicePayable,
  resumeVendorInvoice,
  retryVendorInvoice,
  streamVendorInvoiceReviewChat,
  updateVendorInvoiceDraft,
} from '../../utils/agentsApi';
import VendorInvoicePayablesTable from './vendorInvoice/VendorInvoicePayablesTable';
import VendorInvoiceReviewSplit from './vendorInvoice/VendorInvoiceReviewSplit';
import {
  applyReviewChatEvent,
  buildApprovePayload,
  confirmDeleteJob,
  createEmptyPayable,
  formatIngestionDate,
  formatJobStatusLabel,
  isDynamoPayables,
  isPayableSubmittable,
  pollUntilJobUpdated,
  pollUntilVendorConfirmed,
  JOB_STATUS,
  DELETABLE_JOB_STATUSES,
  PAYABLE_FIELDS,
  SUBMISSION_STATUS,
  VENDOR_INVOICE_POLLING,
  VENDOR_INVOICE_QUERY_KEYS,
  VENDOR_INVOICE_ROUTES,
} from './vendorInvoice/utils';

const pageStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1.5rem',
  paddingBottom: '2rem',
  height: 'calc(100vh - 4rem)',
  minHeight: 0,
  boxSizing: 'border-box',
};

const sectionTitleStyle = {
  fontSize: '1rem',
  fontWeight: 600,
  marginBottom: '0.75rem',
  color: 'var(--black)',
};

const emptyStyle = {
  color: 'var(--darkGrey)',
  fontSize: '0.9rem',
};

const noteStyle = {
  fontSize: '0.875rem',
  color: 'var(--darkGrey)',
  marginBottom: '0.75rem',
};

const btnBase = {
  border: '1px solid var(--lighterGrey)',
  borderRadius: '0.35rem',
  padding: '0.45rem 0.9rem',
  fontSize: '0.875rem',
  cursor: 'pointer',
  background: 'var(--white)',
};

const btnPrimary = {
  ...btnBase,
  background: 'var(--lightBlue)',
  borderColor: 'var(--lightBlue)',
  color: 'var(--white)',
};

const btnDanger = {
  ...btnBase,
  color: 'var(--red)',
  borderColor: 'var(--lightRed)',
};

const statusBadgeStyle = (status) => {
  const base = {
    display: 'inline-block',
    padding: '0.15rem 0.5rem',
    borderRadius: '0.25rem',
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'capitalize',
    background: 'var(--lightestGrey)',
    color: 'var(--darkGrey)',
  };
  if (status === 'finished' || status === 'complete') {
    return { ...base, background: 'var(--lightGreen)', color: 'var(--green)' };
  }
  if (status === 'failed' || status === 'rejected') {
    return { ...base, background: 'var(--lightRed)', color: 'var(--red)' };
  }
  if (
    status?.startsWith('pending') ||
    status === 'processing' ||
    status === 'uncertain' ||
    status === 'incomplete'
  ) {
    return { ...base, background: '#fef9e7', color: 'var(--darkGrey)' };
  }
  return base;
};

const disabledBtn = (style, disabled) => ({
  ...style,
  opacity: disabled ? 0.5 : 1,
  cursor: disabled ? 'not-allowed' : 'pointer',
});

const VendorInvoiceDetail = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [rejectedIndices, setRejectedIndices] = useState(new Set());
  const [chatLoading, setChatLoading] = useState(false);
  const [liveChatMessages, setLiveChatMessages] = useState(null);
  const [awaitingAgent, setAwaitingAgent] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [draftPayables, setDraftPayables] = useState([]);
  const [draftDirty, setDraftDirty] = useState(false);
  const draftPayablesRef = useRef([]);
  const chatAbortRef = useRef(null);
  const draftSavePromiseRef = useRef(null);
  const syncedUpdatedAtRef = useRef(null);

  const queryKey = VENDOR_INVOICE_QUERY_KEYS.job(jobId);

  const fetchJob = () =>
    queryClient.fetchQuery({
      queryKey,
      queryFn: () => fetchVendorInvoice(jobId),
    });

  const {
    data: job,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey,
    queryFn: () => fetchVendorInvoice(jobId),
    enabled: Boolean(jobId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (
        awaitingAgent ||
        status === JOB_STATUS.PROCESSING ||
        status === JOB_STATUS.PENDING
      ) {
        return VENDOR_INVOICE_POLLING.DETAIL_WORKING_MS;
      }
      return false;
    },
    refetchOnWindowFocus: true,
  });

  const payables = job?.payables ?? [];
  const fromDynamo = isDynamoPayables(job);
  // Draft payables are editable while the graph is interrupted for human
  // review. After finalization (or a finalize that failed midway), non-created
  // VIPAYABLE rows can be edited and resubmitted; created rows stay locked.
  const reviewMode = job?.status === JOB_STATUS.PENDING_REVIEW && !fromDynamo;
  const retryMode = Boolean(
    fromDynamo &&
    (job?.status === JOB_STATUS.FINISHED || job?.status === JOB_STATUS.FAILED),
  );
  const payablesEditable = reviewMode || retryMode;

  useEffect(() => {
    draftPayablesRef.current = draftPayables;
  }, [draftPayables]);

  // Sync local draft from server when job updates and local edits are not pending.
  useEffect(() => {
    if (!job) {
      return;
    }
    const serverPayables = Array.isArray(job.payables) ? job.payables : [];
    if (!payablesEditable) {
      setDraftPayables(serverPayables);
      setDraftDirty(false);
      syncedUpdatedAtRef.current = job.updatedAt ?? null;
      return;
    }
    if (draftDirty) {
      return;
    }
    if (syncedUpdatedAtRef.current === job.updatedAt) {
      return;
    }
    setDraftPayables(serverPayables);
    syncedUpdatedAtRef.current = job.updatedAt ?? null;
  }, [job, payablesEditable, draftDirty]);

  const saveDraft = async (payablesToSave = draftPayablesRef.current) => {
    if (!reviewMode || !jobId) {
      return null;
    }
    const savePromise = (async () => {
      const updated = await updateVendorInvoiceDraft(jobId, {
        payables: payablesToSave,
      });
      queryClient.setQueryData(queryKey, updated);
      setDraftPayables(updated?.payables ?? payablesToSave);
      setDraftDirty(false);
      syncedUpdatedAtRef.current = updated?.updatedAt ?? null;
      return updated;
    })();
    draftSavePromiseRef.current = savePromise;
    try {
      return await savePromise;
    } finally {
      if (draftSavePromiseRef.current === savePromise) {
        draftSavePromiseRef.current = null;
      }
    }
  };

  const flushDraftSave = async () => {
    if (draftSavePromiseRef.current) {
      await draftSavePromiseRef.current;
    }
    if (draftDirty) {
      await saveDraft();
    }
  };

  const handlePayablesChange = (nextPayables) => {
    draftPayablesRef.current = nextPayables;
    setDraftPayables(nextPayables);
    setDraftDirty(true);
  };

  // Append a blank hand-entered payable to the draft. It uses the same fields,
  // per-field editing, and validation as parsed rows, and is not submittable
  // until it validates. Persisted on the next commit / accept like any row.
  const handleAddPayable = () => {
    handlePayablesChange([...draftPayablesRef.current, createEmptyPayable()]);
  };

  const handlePayablesCommit = async () => {
    if (!reviewMode) {
      return;
    }
    if (!draftDirty) {
      return;
    }
    try {
      await saveDraft();
    } catch (err) {
      toast.error(err.message || 'Failed to save draft.');
    }
  };

  const { data: vendorListData } = useQuery({
    queryKey: VENDOR_INVOICE_QUERY_KEYS.vendors,
    queryFn: fetchVendorList,
    enabled: job?.status === JOB_STATUS.PENDING_VENDOR,
    staleTime: 5 * 60 * 1000,
  });

  const vendorOptions = vendorListData?.vendors ?? [];

  const confirmVendorMutation = useMutation({
    mutationFn: async (vendorName) => {
      const baseline = job;
      setAwaitingAgent(true);
      try {
        await confirmVendorInvoice(jobId, vendorName);
        return pollUntilVendorConfirmed(fetchJob, baseline);
      } finally {
        setAwaitingAgent(false);
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
      toast.success('Vendor confirmed. Processing started.');
      queryClient.invalidateQueries({
        queryKey: VENDOR_INVOICE_QUERY_KEYS.jobsByStatus(
          JOB_STATUS.PENDING_VENDOR,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: VENDOR_INVOICE_QUERY_KEYS.jobsByStatus(JOB_STATUS.PROCESSING),
      });
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to confirm vendor.');
    },
  });

  const rawChatMessages = liveChatMessages ?? job?.messages ?? [];

  // Filter messages to show only conversational content
  const chatMessages = useMemo(() => {
    return rawChatMessages.filter((msg) => {
      // Hide tool calls and tool results
      if (msg.role === 'tool_call' || msg.role === 'tool') {
        return false;
      }

      // Keep user messages
      if (msg.role === 'user') {
        return true;
      }

      // For assistant/agent messages, only keep if it's actual conversational text
      if (msg.role === 'assistant' || msg.role === 'agent') {
        const text = msg.text || '';
        const trimmedText = text.trim();

        // Hide if empty
        if (!trimmedText) {
          return false;
        }

        // Hide if it looks like JSON data
        if (trimmedText.startsWith('{') || trimmedText.startsWith('[')) {
          return false;
        }

        return true;
      }

      // Hide system messages
      if (msg.role === 'system') {
        return false;
      }

      // Keep any other message types as a fallback
      return true;
    });
  }, [rawChatMessages]);

  const displayPayables = payablesEditable ? draftPayables : payables;

  const activePayables = useMemo(
    () => displayPayables.filter((_, index) => !rejectedIndices.has(index)),
    [displayPayables, rejectedIndices],
  );

  const resubmitMutation = useMutation({
    mutationFn: async (payable) => {
      const payableId = payable?.payableId;
      if (!payableId) {
        throw new Error('Payable id is required to resubmit.');
      }
      const current =
        draftPayablesRef.current.find((p) => p?.payableId === payableId) ??
        payable;
      const fields = {};
      for (const field of PAYABLE_FIELDS) {
        fields[field] = current?.[field];
      }
      return resubmitVendorInvoicePayable(jobId, payableId, fields);
    },
    onSuccess: (data, payable) => {
      queryClient.setQueryData(queryKey, data);
      setDraftPayables(data?.payables ?? []);
      setDraftDirty(false);
      syncedUpdatedAtRef.current = data?.updatedAt ?? null;
      const updated = (data?.payables ?? []).find(
        (p) => p?.payableId === payable?.payableId,
      );
      if (updated?.submissionStatus === SUBMISSION_STATUS.CREATED) {
        toast.success('Payable created successfully.');
      } else if (
        updated?.submissionStatus === SUBMISSION_STATUS.CREATE_FAILED
      ) {
        toast.error(updated.submitError || 'Payable create failed.');
      } else if (
        updated?.submissionStatus === SUBMISSION_STATUS.NOT_SUBMITTED
      ) {
        toast.error('Payable is still incomplete and was not submitted.');
      } else {
        toast.success('Payable updated.');
      }
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to resubmit payable.');
    },
  });

  // Retry all → retryFinalizeHandler queues finalize again → finalizeNode,
  // which files only the payables that have no record yet.
  const retryAllMutation = useMutation({
    mutationFn: async () => {
      const baseline = job;
      await retryVendorInvoice(jobId);
      return pollUntilJobUpdated(fetchJob, baseline);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
      toast.success('Retrying the payables not yet filed…');
      queryClient.invalidateQueries({
        queryKey: VENDOR_INVOICE_QUERY_KEYS.allJobs,
      });
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to retry invoice.');
    },
  });

  const editsDisabled =
    (reviewMode && (chatLoading || awaitingAgent)) ||
    (retryMode && (resubmitMutation.isPending || retryAllMutation.isPending));

  const canAccept =
    reviewMode &&
    activePayables.length > 0 &&
    activePayables.every(isPayableSubmittable) &&
    !chatLoading &&
    !awaitingAgent;

  const acceptMutation = useMutation({
    mutationFn: async () => {
      const baseline = job;
      setAwaitingAgent(true);
      try {
        await flushDraftSave();
        const { payables: approvePayables, rejectedIndices: indices } =
          buildApprovePayload(draftPayablesRef.current, rejectedIndices);
        // APPROVE → approveAndFinalizeHandler queues finalize → the
        // runner resumes the graph → finalizeNode
        await resumeVendorInvoice(jobId, {
          action: 'APPROVE',
          draft: { payables: approvePayables },
          rejectedIndices: indices,
        });
        return pollUntilJobUpdated(fetchJob, baseline);
      } finally {
        setAwaitingAgent(false);
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
      if (data?.status === JOB_STATUS.FINISHED) {
        toast.success('Invoice finalized and payables submitted.');
      } else if (data?.status === JOB_STATUS.FAILED) {
        toast.error(data.failureReason || 'Invoice processing failed.');
      } else {
        toast.success('Invoice approved. Processing submission…');
      }
      queryClient.invalidateQueries({
        queryKey: VENDOR_INVOICE_QUERY_KEYS.allJobs,
      });
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to approve invoice.');
    },
  });

  const handleRejectPayable = (index) => {
    setRejectedIndices((prev) => new Set([...prev, index]));
  };

  const handleUndoReject = (index) => {
    setRejectedIndices((prev) => {
      const next = new Set(prev);
      next.delete(index);
      return next;
    });
  };

  const handleChatSend = async (message) => {
    const trimmed = message.trim();
    if (!trimmed || !jobId) {
      return;
    }

    setChatLoading(true);
    setAwaitingAgent(true);

    const abortController = new AbortController();
    chatAbortRef.current = abortController;

    const base = [...(job?.messages ?? [])];
    const userMsg = {
      id: `user-${Date.now()}`,
      role: 'user',
      text: trimmed,
    };
    setLiveChatMessages([...base, userMsg]);

    try {
      await flushDraftSave();

      for await (const evt of streamVendorInvoiceReviewChat({
        jobId,
        feedback: trimmed,
        signal: abortController.signal,
      })) {
        if (evt.type === 'user_prompt' || evt.type === 'end') {
          continue;
        }

        if (evt.type === 'draft' && Array.isArray(evt.payables)) {
          draftPayablesRef.current = evt.payables;
          setDraftPayables(evt.payables);
          setDraftDirty(false);
          setRejectedIndices(new Set());
          continue;
        }

        if (evt.type === 'error') {
          toast.error(evt.message || 'Review chat failed.');
          continue;
        }

        setLiveChatMessages((prev) =>
          applyReviewChatEvent(prev ?? [...base, userMsg], evt),
        );
      }

      const updated = await fetchJob();
      queryClient.setQueryData(queryKey, updated);
      setDraftPayables(updated?.payables ?? []);
      setDraftDirty(false);
      syncedUpdatedAtRef.current = updated?.updatedAt ?? null;
      setRejectedIndices(new Set());
      setLiveChatMessages(null);
    } catch (err) {
      if (chatAbortRef.current?.signal?.aborted || err.name === 'AbortError') {
        // User cancelled - keep partial messages visible
      } else {
        toast.error(err.message || 'Failed to send feedback.');
        setLiveChatMessages(null);
      }
    } finally {
      chatAbortRef.current = null;
      setChatLoading(false);
      setAwaitingAgent(false);
    }
  };

  const handleChatStop = () => chatAbortRef.current?.abort();

  const deleteMutation = useMutation({
    mutationFn: () => deleteVendorInvoice(jobId),
    onSuccess: () => {
      toast.success('Invoice deleted.');
      queryClient.invalidateQueries({
        queryKey: VENDOR_INVOICE_QUERY_KEYS.allJobs,
      });
      navigate(VENDOR_INVOICE_ROUTES.LIST);
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to delete invoice.');
    },
  });

  const handleDelete = () => {
    if (deleteMutation.isPending) {
      return;
    }
    if (!confirmDeleteJob(job?.vendorName)) {
      return;
    }
    deleteMutation.mutate();
  };

  const handleRetryAll = () => {
    if (retryAllMutation.isPending || resubmitMutation.isPending) {
      return;
    }
    if (
      !window.confirm('Retry submitting the payables not yet filed to HRP?')
    ) {
      return;
    }
    retryAllMutation.mutate();
  };

  const handleConfirmVendor = () => {
    if (!selectedVendor || confirmVendorMutation.isPending) {
      return;
    }
    confirmVendorMutation.mutate(selectedVendor);
  };

  const handleAccept = () => {
    if (!canAccept) {
      return;
    }
    if (
      !window.confirm(
        `Approve and submit ${activePayables.length} payable${activePayables.length === 1 ? '' : 's'} to HRP?`,
      )
    ) {
      return;
    }
    acceptMutation.mutate();
  };

  if (isLoading) {
    return (
      <div className="container" style={pageStyle}>
        <div className="loader" />
      </div>
    );
  }

  if (isError || !job) {
    return (
      <div className="container" style={pageStyle}>
        <p style={{ color: 'var(--red)', marginBottom: '1rem' }}>
          {error?.message || 'Invoice not found.'}
        </p>
        <Link to={VENDOR_INVOICE_ROUTES.LIST} className="clickable-link">
          Back to list
        </Link>
      </div>
    );
  }

  const chatDisabled = !reviewMode || chatLoading || awaitingAgent;
  const isFailed = job.status === JOB_STATUS.FAILED;
  const showRetryAll = isFailed && Boolean(job.finalizeRetryable);
  const retryAllDisabled =
    retryAllMutation.isPending || resubmitMutation.isPending;
  const isPendingVendor = job.status === JOB_STATUS.PENDING_VENDOR;
  const isDeletable = DELETABLE_JOB_STATUSES.includes(job.status);
  const statusLabel = formatJobStatusLabel(job.status);
  const showAcceptButton = reviewMode;
  const confirmDisabled =
    !selectedVendor || confirmVendorMutation.isPending || awaitingAgent;
  const acceptDisabled = !canAccept || acceptMutation.isPending;

  return (
    <div className="container" style={pageStyle}>
      {isFailed && (
        <p
          style={{
            fontSize: '0.875rem',
            color: 'var(--red)',
            background: 'var(--lightRed)',
            border: '1px solid var(--red)',
            borderRadius: '0.375rem',
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
          }}
        >
          Processing failed: {job.failureReason || 'Unknown error'}
        </p>
      )}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '1rem',
          flexWrap: 'wrap',
          flexShrink: 0,
        }}
      >
        <div>
          <Link
            to={VENDOR_INVOICE_ROUTES.LIST}
            className="clickable-link"
            style={{
              display: 'inline-block',
              marginBottom: '0.5rem',
              fontSize: '0.875rem',
            }}
          >
            ← Back to list
          </Link>
          <p className="title">{job.vendorName || 'Unknown vendor'}</p>
          <p className="sub-title">
            Invoice
            {job.billingPeriod ? ` · ${job.billingPeriod}` : ''}
            {' · '}
            Ingested {formatIngestionDate(job.createdAt)}
          </p>
          {job.updatedBy && (
            <p className="sub-title">Last touched by {job.updatedBy}</p>
          )}
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '0.35rem',
          }}
        >
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}
          >
            <span style={statusBadgeStyle(job.status)}>{statusLabel}</span>
            {isDeletable && (
              <button
                type="button"
                style={disabledBtn(btnDanger, deleteMutation.isPending)}
                disabled={deleteMutation.isPending}
                onClick={handleDelete}
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </button>
            )}
            {showRetryAll && (
              <button
                type="button"
                style={disabledBtn(btnPrimary, retryAllDisabled)}
                disabled={retryAllDisabled}
                onClick={handleRetryAll}
              >
                {retryAllMutation.isPending ? 'Retrying…' : 'Retry all'}
              </button>
            )}
            {showAcceptButton && (
              <button
                type="button"
                style={disabledBtn(btnPrimary, acceptDisabled)}
                disabled={acceptDisabled}
                onClick={handleAccept}
              >
                {acceptMutation.isPending ? 'Submitting…' : 'Accept & Submit'}
              </button>
            )}
          </div>
          {showAcceptButton &&
            acceptDisabled &&
            !acceptMutation.isPending &&
            !chatLoading &&
            !awaitingAgent && (
              <p
                style={{
                  margin: 0,
                  fontSize: '0.75rem',
                  color: 'var(--darkGrey)',
                  textAlign: 'right',
                  maxWidth: '16rem',
                }}
              >
                {activePayables.length === 0
                  ? 'Reject at least one fewer payable, or undo a rejection.'
                  : `${
                      activePayables.filter((p) => !isPayableSubmittable(p))
                        .length || activePayables.length
                    } payable(s) still need fixes or rejection`}
              </p>
            )}
        </div>
      </div>

      <VendorInvoiceReviewSplit
        autoSaveId="vendor-invoice-review"
        style={{ flex: 1, minHeight: 0 }}
        left={
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1.5rem',
              paddingBottom: '0.5rem',
            }}
          >
            <section>
              <h2 style={sectionTitleStyle}>Source files</h2>
              {job.files?.length ? (
                <ul
                  style={{
                    listStyle: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.35rem',
                    margin: 0,
                    padding: 0,
                  }}
                >
                  {job.files.map((file) => (
                    <li key={file.key}>
                      {file.downloadUrl ? (
                        <a
                          href={file.downloadUrl}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            color: 'var(--lightBlue)',
                            textDecoration: 'none',
                          }}
                        >
                          {file.name}
                        </a>
                      ) : (
                        <span>{file.name}</span>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p style={emptyStyle}>No files attached.</p>
              )}
              {isPendingVendor && (
                <div
                  style={{
                    marginTop: '1rem',
                    paddingTop: '1rem',
                    borderTop: '1px solid var(--lighterGrey)',
                  }}
                >
                  <label
                    htmlFor="vendor-select"
                    style={{
                      display: 'block',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      marginBottom: '0.5rem',
                    }}
                  >
                    Select vendor
                  </label>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: '0.75rem',
                    }}
                  >
                    <select
                      id="vendor-select"
                      style={{
                        flex: 1,
                        minWidth: 200,
                        maxWidth: 360,
                        padding: '0.5rem 0.75rem',
                        border: '1px solid var(--lighterGrey)',
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem',
                        background: 'var(--white)',
                      }}
                      value={selectedVendor}
                      onChange={(e) => setSelectedVendor(e.target.value)}
                      disabled={
                        confirmVendorMutation.isPending || awaitingAgent
                      }
                    >
                      <option value="">Choose a vendor…</option>
                      {vendorOptions.map((v) => (
                        <option key={v.name} value={v.name}>
                          {v.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      style={disabledBtn(btnPrimary, confirmDisabled)}
                      disabled={confirmDisabled}
                      onClick={handleConfirmVendor}
                    >
                      {confirmVendorMutation.isPending
                        ? 'Confirming…'
                        : 'Confirm'}
                    </button>
                  </div>
                  <p
                    style={{
                      marginTop: '0.5rem',
                      fontSize: '0.8125rem',
                      color: 'var(--darkGrey)',
                    }}
                  >
                    This invoice could not be matched automatically. Select the
                    correct vendor to begin processing.
                  </p>
                </div>
              )}
            </section>

            <section>
              <h2 style={sectionTitleStyle}>Payables</h2>
              {isPendingVendor && (
                <p style={noteStyle}>Select a vendor to begin processing.</p>
              )}
              {(job.status === JOB_STATUS.PROCESSING ||
                job.status === JOB_STATUS.PENDING ||
                awaitingAgent) &&
                !isPendingVendor && (
                  <p style={noteStyle}>
                    {job.status === JOB_STATUS.PENDING
                      ? 'Invoice is queued and will start processing shortly.'
                      : 'Agent is processing — payables will update automatically.'}
                  </p>
                )}
              {job.status === JOB_STATUS.FINISHED && (
                <p style={noteStyle}>
                  This invoice has been finalized.
                  {retryMode
                    ? ' Failed, rejected, or incomplete payables can be edited and resubmitted.'
                    : null}{' '}
                  <button
                    type="button"
                    className="clickable-link"
                    onClick={() => navigate('/finance/vendor-invoices')}
                  >
                    Return to list
                  </button>
                </p>
              )}
              {isFailed && (
                <p style={noteStyle}>
                  {showRetryAll
                    ? 'Submission stopped partway. Retry all files the payables not yet filed; failed or incomplete payables can also be edited and resubmitted.'
                    : 'This invoice cannot be processed further.'}{' '}
                  <button
                    type="button"
                    className="clickable-link"
                    onClick={() => navigate('/finance/vendor-invoices')}
                  >
                    Return to list
                  </button>
                </p>
              )}
              <VendorInvoicePayablesTable
                payables={displayPayables}
                rejectedIndices={reviewMode ? rejectedIndices : new Set()}
                onReject={reviewMode ? handleRejectPayable : undefined}
                onUndoReject={reviewMode ? handleUndoReject : undefined}
                readOnly={!payablesEditable}
                retryMode={retryMode}
                editsDisabled={editsDisabled}
                onPayablesChange={
                  payablesEditable ? handlePayablesChange : undefined
                }
                onPayablesCommit={reviewMode ? handlePayablesCommit : undefined}
                onAddPayable={reviewMode ? handleAddPayable : undefined}
                onResubmit={
                  retryMode
                    ? (payable) => resubmitMutation.mutate(payable)
                    : undefined
                }
                resubmittingPayableId={
                  resubmitMutation.isPending
                    ? resubmitMutation.variables?.payableId
                    : null
                }
                showValidation={payablesEditable}
                isPreviewMode={Boolean(job?.previewMode)}
              />
            </section>
          </div>
        }
        chatProps={{
          disabled: chatDisabled,
          loading:
            chatLoading ||
            job.status === JOB_STATUS.PROCESSING ||
            awaitingAgent,
          messages: chatMessages,
          onSend: handleChatSend,
          onStop: handleChatStop,
        }}
      />
    </div>
  );
};

export default VendorInvoiceDetail;
